import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { recipientReminderBundleSchema } from "./shapes";

/**
 * Query Due Collection Follow-ups
 *
 * (Step type keeps its original `query_stale_collection_threads` id for
 * compatibility with persisted workflow definitions.)
 *
 * Finds every OPEN collection thread whose "Set next step" follow-up date has
 * come due, is past its reminder gate, and has an assignee — then routes each
 * to that assignee. Only explicitly parked, owned threads are reminded;
 * staleness alone and unassigned threads never notify. Emits one bundle per
 * recipient carrying only the threads routed to them, as the `reminders`
 * array. Wire that to a single-object input on the next step to fan out one
 * notification per recipient (so each can be reached on their own preferred
 * channels).
 *
 * Threads included in the result have their reminder gate pushed forward by
 * `cooldownDays` as part of this step, so the same thread won't be re-reminded
 * until the cooldown elapses.
 */
export const queryStaleCollectionThreadsStep = defineStepConfig({
  type: "query_stale_collection_threads",
  name: "Query Due Collection Follow-ups",
  description:
    "Find every open collection thread whose Set-next-step follow-up date " +
    "has come due and that has an assignee, and route it to that assignee. " +
    "Unassigned threads and threads still within their reminder cooldown " +
    "are skipped.",
  category: "Data Access",
  libraryModule: "AccountsReceivable",

  // No data inputs — just the implicit flow signal from the schedule trigger.
  inputSchema: z.object({}),

  outputSchema: z.object({
    reminders: z
      .array(recipientReminderBundleSchema)
      .describe(
        "One bundle per assignee, each with the threads whose follow-up came " +
          "due for them. Connect to a single-object input (e.g. Prepare " +
          "Collection Reminder) to fan out one notification per recipient.",
      ),
    recipientCount: z
      .number()
      .int()
      .nonnegative()
      .describe("Number of assignees who will be notified."),
    threadCount: z
      .number()
      .int()
      .nonnegative()
      .describe("Number of distinct due threads in the digest."),
  }),

  configSchema: z.object({
    cooldownDays: z
      .number()
      .int()
      .positive()
      .default(3)
      .describe(
        "After a thread is included in a reminder, suppress further reminders " +
          "for this many days (the reminder gate).",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "60 seconds",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
